import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { aiaAwardsAdminQueryOptions, type AiaAward } from "@/lib/aia-awards.queries";
import { createAiaAward, deleteAiaAward, updateAiaAward } from "@/lib/aia-awards.functions";
import { internalHrefFor, isInternalHref, storedLinkProblem } from "@/lib/legacy-redirects";
import { MEDIA_TARGETS } from "@/lib/media-buckets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ExternalLink, Image as ImageIcon, Award } from "lucide-react";
import { MediaField } from "@/components/admin/MediaField";
import { Field } from "@/components/admin/Field";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";

export const Route = createFileRoute("/_authenticated/admin/awards")({
  component: AwardsAdmin,
});

type Category = "industry" | "leadership";

type FormState = {
  id?: string;
  category: Category;
  name: string;
  description: string;
  image_path: string;
  article_url: string;
  display_order: number;
  is_active: boolean;
};

const emptyForm: FormState = {
  category: "industry",
  name: "",
  description: "",
  image_path: "",
  article_url: "",
  display_order: 0,
  is_active: true,
};

/**
 * The list shows where a stored link really lands: the page on this site
 * (old miinews.com addresses are translated the way the public pages do it)
 * or "Open" for an outside address. Always a new tab, so the list stays put.
 */
function ArticleLinkCell({ url }: { url: string }) {
  const href = internalHrefFor(url);
  const onSite = isInternalHref(href);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={onSite && href !== url ? `Stored as ${url}` : undefined}
      className="inline-flex max-w-[20rem] items-center gap-1 hover:underline"
    >
      {onSite ? <span className="truncate">{href}</span> : "Open"}
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}

function AwardsAdmin() {
  const { data: rows = [] } = useQuery(aiaAwardsAdminQueryOptions());
  const qc = useQueryClient();
  const create = useServerFn(createAiaAward);
  const update = useServerFn(updateAiaAward);
  const del = useServerFn(deleteAiaAward);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  // True while the MediaField's Upload/Library picker (sibling modal) is up.
  const [libraryOpen, setLibraryOpen] = useState(false);
  const dirty = useDirty(form, open);

  const sorted = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          a.category.localeCompare(b.category) ||
          a.display_order - b.display_order ||
          a.name.localeCompare(b.name),
      ),
    [rows],
  );

  const payloadOf = (f: FormState) => ({
    category: f.category,
    name: f.name.trim(),
    description: f.description || null,
    image_path: f.image_path || null,
    article_url: f.article_url.trim() || null,
    display_order: f.display_order,
    is_active: f.is_active,
  });

  // Same rule the API applies on save (people-api awardSchema).
  const linkProblem = storedLinkProblem(form.article_url);

  const saveMut = useMutation({
    mutationFn: async (f: FormState) =>
      f.id ? update({ data: { ...payloadOf(f), id: f.id } }) : create({ data: payloadOf(f) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["aia_awards"] });
      setOpen(false);
      setForm(emptyForm);
      toast.success("Saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const toggleMut = useMutation({
    mutationFn: (a: AiaAward) =>
      update({
        data: {
          id: a.id,
          category: a.category,
          name: a.name,
          description: a.description,
          image_path: a.image_path,
          article_url: a.article_url,
          display_order: a.display_order,
          is_active: !a.is_active,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["aia_awards"] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["aia_awards"] });
      toast.success("Removed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const openEdit = (a: AiaAward) => {
    setForm({
      id: a.id,
      category: a.category,
      name: a.name,
      description: a.description ?? "",
      image_path: a.image_path ?? "",
      article_url: a.article_url ?? "",
      display_order: a.display_order,
      is_active: a.is_active,
    });
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>AIA Awards</CardTitle>
          <CardDescription>
            Industry and Leadership awards shown on the public Awards page.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => { setForm({ ...emptyForm }); setOpen(true); }}>
          <Plus className="mr-1 h-4 w-4" /> Add award
        </Button>
        <FormDialog
          open={open}
          onOpenChange={setOpen}
          size="md"
          icon={Award}
          title={form.id ? "Edit award" : "Add award"}
          context={form.id ? form.name : "New award"}
          dirty={dirty}
          busy={libraryOpen || saveMut.isPending}
          asForm
          onSubmit={() => {
            if (linkProblem) {
              toast.error(linkProblem);
              return;
            }
            saveMut.mutate(form);
          }}
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <SaveButton dirty={dirty} pending={saveMut.isPending} />
            </>
          }
        >
          <div className="space-y-4">
            <FormSection title="Award">
              <Field label="Category">
                <Select
                  value={form.category}
                  onValueChange={(v: Category) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="industry">Industry Award</SelectItem>
                    <SelectItem value="leadership">Leadership Award</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Display order">
                <Input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) || 0 })}
                />
              </Field>
              <Field label="Name / recipient" required full>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <div className="sm:col-span-2">
                <MediaField
                  label="Image"
                  value={form.image_path}
                  target={MEDIA_TARGETS.awardImage}
                  onChange={(url) => setForm((f) => ({ ...f, image_path: url }))}
                  onPickerOpenChange={setLibraryOpen}
                />
              </div>
            </FormSection>
            <FormSection title="Details">
              <Field label="Article link" full>
                <Input
                  type="text"
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  value={form.article_url}
                  aria-invalid={linkProblem ? true : undefined}
                  aria-describedby={linkProblem ? "aw-link-error" : "aw-link-hint"}
                  onChange={(e) => setForm({ ...form, article_url: e.target.value })}
                  placeholder="/news/article-slug"
                />
                {linkProblem ? (
                  <p id="aw-link-error" className="text-xs text-destructive">
                    {linkProblem}
                  </p>
                ) : (
                  <p id="aw-link-hint" className="text-xs text-muted-foreground">
                    A page on this site, such as /news/article-slug, or a full address starting with
                    https://
                  </p>
                )}
              </Field>
              <Field label="Description" full>
                <Textarea
                  rows={5}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Field>
              <Field label="Status">
                <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
                  <Switch
                    id="aw-active"
                    checked={form.is_active}
                    onCheckedChange={(v: boolean) => setForm({ ...form, is_active: v })}
                  />
                  <Label htmlFor="aw-active" className="cursor-pointer">
                    {form.is_active ? "Active" : "Inactive"}
                  </Label>
                </div>
              </Field>
            </FormSection>
          </div>
        </FormDialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-32">Category</TableHead>
              <TableHead className="hidden md:table-cell">Article</TableHead>
              <TableHead className="w-20 text-center">Active</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((a) => (
              <TableRow key={a.id} className={a.is_active ? "" : "opacity-60"}>
                <TableCell>
                  {a.image_path ? (
                    <img
                      src={a.image_path}
                      alt=""
                      className="h-8 w-8 rounded bg-white object-contain"
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-muted text-muted-foreground">
                      <ImageIcon className="h-4 w-4" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell>
                  <Badge variant={a.category === "industry" ? "secondary" : "default"}>
                    {a.category === "industry" ? "Industry" : "Leadership"}
                  </Badge>
                </TableCell>
                <TableCell className="hidden text-muted-foreground md:table-cell">
                  {a.article_url ? <ArticleLinkCell url={a.article_url} /> : "—"}
                </TableCell>
                <TableCell className="text-center">
                  <Switch checked={a.is_active} onCheckedChange={() => toggleMut.mutate(a)} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(a)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm(`Remove "${a.name}"?`)) delMut.mutate(a.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No awards yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

